import api from './api';

export interface OpenSession {
  _id: string;
  weekNumber: number;
  date: string;
  startTime: string;
  endTime: string;
  isActive: boolean;
  maxAttendees?: number;
  currentAttendees: number;
  notes?: string;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  isAdhoc?: boolean; // Flag to identify ad-hoc sessions
  isAuction?: boolean; // Flag to identify auction sessions
  attendees?: Array<{
    name: string;
    email?: string;
    phone: string;
    registeredAt: string;
  }>;
}

export interface OpenSchedule {
  _id: string;
  propertyId: string;
  agencyId: string;
  
  // Sale type and auction configuration
  saleType: 'sale' | 'auction' | 'rental';
  isAuction: boolean;
  
  // Schedule configuration
  totalWeeks: number;
  startDate: string;
  endDate: string;
  
  // Day selection for openings
  weekDays: number[]; // Days of the week (0-6, Sunday-Saturday)
  
  // Flexible scheduling for private treaty
  isFlexible: boolean;
  maxWeeks: number;
  autoExtend: boolean;
  
  // Default times for openings
  defaultStartTime: string;
  defaultEndTime: string;
  
  // Schedule status
  status: 'draft' | 'active' | 'completed' | 'cancelled' | 'extended';
  
  // Individual opening sessions
  sessions: OpenSession[];
  
  // Auction details
  auctionDate?: string;
  auctionTime?: string;
  auctionLocation?: string;
  auctionStatus?: 'scheduled' | 'completed' | 'failed' | 'cancelled';
  auctionResult?: 'sold' | 'passed_in' | 'cancelled';
  reservePrice?: number;
  
  // Extension tracking
  originalEndDate?: string;
  extensionCount: number;
  lastExtendedDate?: string;
  
  // Notes and settings
  notes?: string;
  maxAttendeesPerSession?: number;
  requireRegistration: boolean;
  
  // Timestamps
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface CreateOpenScheduleData {
  propertyId: string;
  totalWeeks: number;
  startDate: string;
  endDate: string;
  weekDays: number[]; // Days of the week (0-6, Sunday-Saturday)
  isFlexible?: boolean;
  maxWeeks?: number;
  autoExtend?: boolean;
  defaultStartTime: string;
  defaultEndTime: string;
  auctionDate?: string;
  auctionTime?: string;
  auctionLocation?: string;
  reservePrice?: number;
  notes?: string;
  maxAttendeesPerSession?: number;
  requireRegistration?: boolean;

  // Optional explicit sessions; when provided, the backend will
  // use these instead of generating a repeating pattern.
  sessions?: Array<{
    date: string;
    startTime: string;
    endTime: string;
  }>;
}

export interface UpdateOpenScheduleData extends Partial<CreateOpenScheduleData> {}

export interface ScheduleStats {
  totalSessions: number;
  completedSessions: number;
  upcomingSessions: number;
  cancelledSessions: number;
  totalAttendees: number;
  averageAttendees: number;
  extensionCount: number;
  isExtended: boolean;
  auctionStatus?: string;
  auctionResult?: string;
}

export interface AuctionResultData {
  result: 'sold' | 'passed_in' | 'cancelled';
  reservePrice?: number;
}

class OpenScheduleService {
  constructor() {
    // Service initialization
  }

  // Get open schedule for a property
  async getOpenSchedule(propertyId: string, agencySlug: string, includePastSessions?: boolean): Promise<OpenSchedule> {
    const params = includePastSessions ? { includePastSessions: 'true' } : {};
    const url = `/agencies/${agencySlug}/properties/${propertyId}/open-schedule`;
    console.log('openScheduleService.getOpenSchedule:');
    console.log('  - URL:', url);
    console.log('  - includePastSessions:', includePastSessions);
    console.log('  - params:', params);
    const fullUrl = params && Object.keys(params).length > 0 
      ? `${url}?${new URLSearchParams(params as Record<string, string>).toString()}`
      : url;
    console.log('  - Full URL with params:', fullUrl);
    
    try {
      const response = await api.get(url, { params });
      console.log('openScheduleService.getOpenSchedule: Response received');
      console.log('  - Status:', response.status);
      console.log('  - Sessions count:', response.data.data?.sessions?.length || 0);
      console.log('  - Schedule _id:', response.data.data?._id);
      return response.data.data;
    } catch (error: any) {
      console.error('openScheduleService.getOpenSchedule: Error:', error);
      console.error('  - Message:', error.message);
      console.error('  - Response:', error.response?.data);
      console.error('  - Status:', error.response?.status);
      throw error;
    }
  }

  // Create new open schedule
  async createOpenSchedule(scheduleData: CreateOpenScheduleData, agencySlug: string): Promise<OpenSchedule> {
    try {
      const response = await api.post(`/agencies/${agencySlug}/properties/${scheduleData.propertyId}/open-schedules`, scheduleData);
      // Handle both response.data.data and response.data structures
      return response.data.data || response.data;
    } catch (error: any) {
      // Extract error message from response
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create open schedule';
      throw new Error(errorMessage);
    }
  }

  // Update open schedule
  async updateOpenSchedule(scheduleId: string, updateData: UpdateOpenScheduleData, agencySlug: string): Promise<OpenSchedule> {
    const response = await api.put(`/agencies/${agencySlug}/open-schedules/${scheduleId}`, updateData);
    return response.data.data;
  }

  // Update individual session
  async updateSession(scheduleId: string, sessionId: string, updateData: any, agencySlug: string): Promise<OpenSession> {
    const response = await api.put(`/agencies/${agencySlug}/open-schedules/${scheduleId}/sessions/${sessionId}`, updateData);
    return response.data.data;
  }

  async addAdhocSession(scheduleId: string, sessionData: {
    date: string;
    startTime: string;
    endTime: string;
    notes?: string;
    maxAttendees?: number;
  }, agencySlug: string): Promise<OpenSession> {
    const response = await api.post(`/agencies/${agencySlug}/open-schedules/${scheduleId}/sessions/adhoc`, sessionData);
    return response.data.data;
  }

  // Register attendee for a session
  async registerAttendee(scheduleId: string, sessionId: string, attendeeData: {
    name: string;
    email?: string;
    phone: string;
  }, agencySlug: string): Promise<OpenSession> {
    const response = await api.post(`/agencies/${agencySlug}/open-schedules/${scheduleId}/sessions/${sessionId}/register`, attendeeData);
    return response.data.data;
  }

  // Get upcoming sessions for agency
  async getUpcomingSessions(agencySlug: string, page: number = 1, limit: number = 10): Promise<{
    data: any[];
    pagination: {
      page: number;
      limit: number;
      total: number;
    };
  }> {
    const response = await api.get(`/agencies/${agencySlug}/open-schedules/upcoming?page=${page}&limit=${limit}`);
    return response.data;
  }

  // Activate schedule
  async activateSchedule(scheduleId: string, agencySlug: string): Promise<OpenSchedule> {
    const response = await api.post(`/agencies/${agencySlug}/open-schedules/${scheduleId}/activate`);
    return response.data.data;
  }

  // Cancel schedule
  async cancelSchedule(scheduleId: string, agencySlug: string, reason?: string): Promise<OpenSchedule> {
    const response = await api.post(`/agencies/${agencySlug}/open-schedules/${scheduleId}/cancel`, { reason });
    return response.data.data;
  }

  // Extend schedule (for private treaty)
  async extendSchedule(scheduleId: string, agencySlug: string, additionalWeeks: number = 2): Promise<OpenSchedule> {
    const response = await api.post(`/agencies/${agencySlug}/open-schedules/${scheduleId}/extend`, { additionalWeeks });
    return response.data.data;
  }

  // Handle auction result
  async handleAuctionResult(scheduleId: string, agencySlug: string, auctionData: AuctionResultData): Promise<OpenSchedule> {
    const response = await api.post(`/agencies/${agencySlug}/open-schedules/${scheduleId}/auction-result`, auctionData);
    return response.data.data;
  }

  // Get schedule statistics
  async getScheduleStats(scheduleId: string, agencySlug: string): Promise<ScheduleStats> {
    const response = await api.get(`/agencies/${agencySlug}/open-schedules/${scheduleId}/stats`);
    return response.data.data;
  }

  // Get all attendees across all properties
  async getAllAttendees(
    agencySlug: string,
    filters?: { search?: string; tags?: string[]; saleTypeGroup?: 'sale' | 'rental' }
  ): Promise<{
    attendees: Array<{
      name: string;
      email?: string;
      phone: string;
      registeredAt: string;
      firstVisit: string;
      property: {
        address?: any;
        suburb?: string;
        state?: string;
        postcode?: string;
      };
      visits: number;
      properties: string[];
      tags?: string[];
    }>;
    totalAttendees: number;
    totalVisits: number;
  }> {
    const queryParams = new URLSearchParams();
    if (filters?.search) queryParams.append('search', filters.search);
    if (filters?.tags && filters.tags.length > 0) {
      queryParams.append('tags', filters.tags.join(','));
    }
    if (filters?.saleTypeGroup) {
      queryParams.append('saleTypeGroup', filters.saleTypeGroup);
    }

    const qs = queryParams.toString();
    const url =
      qs.length > 0
        ? `/agencies/${agencySlug}/open-schedules/attendees?${qs}`
        : `/agencies/${agencySlug}/open-schedules/attendees`;
    const response = await api.get(url);
    return response.data.data;
  }

  // Helper method to format date for display (Australian format)
  formatDate(date: string): string {
    return new Date(date).toLocaleDateString('en-AU', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  // Helper method to format time for display
  formatTime(time: string): string {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-AU', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  // Helper method to get session status color
  getSessionStatusColor(status: string): string {
    switch (status) {
      case 'scheduled': return '#1976d2';
      case 'in_progress': return '#f57c00';
      case 'completed': return '#388e3c';
      case 'cancelled': return '#d32f2f';
      default: return '#757575';
    }
  }

  // Helper method to get schedule status color
  getScheduleStatusColor(status: string): string {
    switch (status) {
      case 'draft': return '#757575';
      case 'active': return '#1976d2';
      case 'extended': return '#f57c00';
      case 'completed': return '#388e3c';
      case 'cancelled': return '#d32f2f';
      default: return '#757575';
    }
  }

  // Helper method to check if schedule can be extended
  canExtend(schedule: OpenSchedule): boolean {
    return schedule.isFlexible && 
           schedule.extensionCount < 5 && 
           schedule.status !== 'completed' && 
           schedule.status !== 'cancelled';
  }

  // Helper method to check if auction result can be recorded
  canRecordAuctionResult(schedule: OpenSchedule): boolean {
    return Boolean(schedule.isAuction && 
           schedule.auctionStatus === 'scheduled' && 
           schedule.auctionDate && 
           new Date(schedule.auctionDate) <= new Date());
  }

  // Helper method to get next session
  getNextSession(schedule: OpenSchedule): OpenSession | null {
    const now = new Date();
    const upcomingSessions = schedule.sessions.filter(session => {
      // Include scheduled or in-progress sessions
      if (session.status !== 'scheduled' && session.status !== 'in_progress') {
        return false;
      }
      
      // Create session date-time from date and startTime
      const sessionDate = new Date(session.date);
      if (!sessionDate || isNaN(sessionDate.getTime())) {
        return false;
      }
      
      if (session.startTime) {
        const [hours, minutes] = session.startTime.split(':').map(Number);
        sessionDate.setHours(hours || 0, minutes || 0, 0, 0);
      } else {
        sessionDate.setHours(0, 0, 0, 0);
      }
      
      // Include if session is in the future or currently in progress
      return sessionDate >= now || session.status === 'in_progress';
    });
    
    // Sort by date/time and return the first one
    upcomingSessions.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      if (a.startTime) {
        const [hoursA, minutesA] = a.startTime.split(':').map(Number);
        dateA.setHours(hoursA || 0, minutesA || 0, 0, 0);
      }
      if (b.startTime) {
        const [hoursB, minutesB] = b.startTime.split(':').map(Number);
        dateB.setHours(hoursB || 0, minutesB || 0, 0, 0);
      }
      return dateA.getTime() - dateB.getTime();
    });
    
    return upcomingSessions.length > 0 ? upcomingSessions[0] : null;
  }

  // Helper method to get today's sessions
  getTodaySessions(schedule: OpenSchedule): OpenSession[] {
    const now = new Date();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return schedule.sessions.filter(session => {
      const sessionDate = new Date(session.date);
      if (!sessionDate || isNaN(sessionDate.getTime())) {
        return false;
      }
      
      // Check if session is today
      const sessionDateOnly = new Date(sessionDate);
      sessionDateOnly.setHours(0, 0, 0, 0);
      
      if (sessionDateOnly.getTime() !== today.getTime()) {
        return false;
      }
      
      // For today's sessions, only include future or in-progress ones
      if (session.startTime) {
        const [hours, minutes] = session.startTime.split(':').map(Number);
        sessionDate.setHours(hours || 0, minutes || 0, 0, 0);
      } else {
        sessionDate.setHours(0, 0, 0, 0);
      }
      
      // Include if it's today and hasn't started yet, or is in progress
      return sessionDate >= now || session.status === 'in_progress';
    });
  }

  // Get assignee availability for scheduling suggestions
  async getAssigneeAvailability(
    agencySlug: string,
    params: {
      propertyId: string;
      startDate?: string;
      endDate?: string;
      weekDays?: number[];
      startTime?: string;
      endTime?: string;
    }
  ): Promise<{
    propertyId: string;
    assignees: string[];
    assigneeDetails: Array<{
      id: string;
      name: string;
      email: string;
    }>;
    totalAssignees: number;
    dateRange: { start: Date; end: Date };
    suggestions: Array<{
      date: string;
      dayOfWeek: number;
      dayName: string;
      startTime: string;
      endTime: string;
      status: 'optimal' | 'available' | 'partial' | 'conflict' | 'unavailable';
      availableAssignees: string[];
      assigneeAvailability: Array<{
        id: string;
        name: string;
        email: string;
        available: boolean;
        conflicts: Array<{
          title: string;
          startTime: string;
          endTime: string;
          eventType: string;
        }>;
      }>;
      conflicts: Array<{
        userId: string;
        title: string;
        startTime: string;
        endTime: string;
        eventType: string;
      }>;
      score: number;
    }>;
    summary: {
      total: number;
      optimal: number;
      available: number;
      partial: number;
      conflicts: number;
    };
  }> {
    const queryParams = new URLSearchParams();
    queryParams.append('propertyId', params.propertyId);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.weekDays) queryParams.append('weekDays', params.weekDays.join(','));
    if (params.startTime) queryParams.append('startTime', params.startTime);
    if (params.endTime) queryParams.append('endTime', params.endTime);

    const response = await api.get(`/agencies/${agencySlug}/availability?${queryParams.toString()}`);
    return response.data.data;
  }
}

export default new OpenScheduleService(); 